import React, { useEffect, useRef, useState } from 'react';
import { Button, CircularProgress, Icon, IconButton, ToggleButton } from '@mui/material';
import clsx from 'clsx';
import { useScreen } from 'global/styles';
import {
  FilterBar as StyledFilterBar,
  FilterControl as StyledFilterControl,
  FilterRatingGroup,
  FilterResetWrap,
  FilterSearchControl,
  FilterSearchInput,
  FilterSelect as StyledFilterSelect,
  RatingIconWrap,
} from './styles';

export { FILTER_HEIGHT } from './styles';

export function FilterBar({ flush, inset, className, ...props }) {
  return (
    <StyledFilterBar
      className={clsx(flush && 'flush', inset === true && 'inset', typeof inset === 'number' && `inset-${inset}`, className)}
      {...props}
    />
  );
}

export function FilterControl({ active, iconOnly, className, ...props }) {
  return <StyledFilterControl className={clsx(active && 'active', iconOnly && 'icon-only', className)} {...props} />;
}

export function FilterSelect({ isSelected, className, ...props }) {
  return <StyledFilterSelect className={clsx(isSelected && 'selected', className)} {...props} />;
}

const optionValue = (opt) => (opt && (opt.value ?? opt.val ?? opt.id));
const optionLabel = (opt) => (opt && (opt.label ?? opt.text ?? opt.title));

const asIcon = (icon) => {
  if (!icon) return null;
  if (typeof icon === 'string') return <Icon>{icon}</Icon>;
  return icon;
};

export function FilterButton({ active, children, icon, startIcon, endIcon, disabled, onClick, 'aria-label': ariaLabel }) {
  const iconOnly = !children;
  const start = asIcon(startIcon || icon);
  return (
    <FilterControl active={!!active} iconOnly={iconOnly}>
      <Button
        variant={active ? 'contained' : 'text'}
        color={active ? 'primary' : 'inherit'}
        disabled={disabled}
        onClick={onClick}
        startIcon={start}
        endIcon={endIcon}
        disableElevation
        size="small"
        aria-pressed={!!active}
        aria-label={ariaLabel}
        sx={iconOnly ? { '& .MuiButton-startIcon, & .MuiButton-endIcon': { mx: 0 } } : undefined}
      >
        {children}
      </Button>
    </FilterControl>
  );
}

export function FilterReset({ onClick, visible }) {
  return (
    <FilterResetWrap className={clsx(visible && 'open')} aria-hidden={!visible}>
      <FilterButton onClick={onClick} icon="replay" aria-label="Reset filters" />
    </FilterResetWrap>
  );
}

export function FilterGroupToggle({ value, defaultValue, options = [], onChange, disabled }) {
  const current = options.find((o) => optionValue(o) === value) || options[0];
  const isAlternate = value !== defaultValue;
  const next = options.find((o) => optionValue(o) !== optionValue(current));
  const label = optionLabel(current);

  return (
    <FilterButton
      active={isAlternate}
      disabled={disabled || !next}
      onClick={() => next && onChange(optionValue(next))}
      endIcon={<Icon>{isAlternate ? 'arrow_drop_up' : 'arrow_drop_down'}</Icon>}
      aria-label={label}
    >
      {label}
    </FilterButton>
  );
}

export function FilterSearch({ searchString = '', onChange, onClear, loading, placeholder = 'Search...' }) {
  const { isMobileXS } = useScreen();
  const [expanded, setExpanded] = useState(!!searchString);
  const inputRef = useRef(null);
  const showInput = !isMobileXS || expanded || !!searchString;

  useEffect(() => {
    if (searchString) {
      setExpanded(true);
    }
  }, [searchString]);

  const expandSearch = () => {
    setExpanded(true);
    requestAnimationFrame(() => inputRef.current?.focus());
  };

  const clearSearch = () => {
    onClear ? onClear() : onChange('');
    if (isMobileXS) {
      setExpanded(false);
    }
  };

  if (!showInput) {
    return <FilterButton onClick={expandSearch} icon="search" aria-label="Search" />;
  }

  return (
    <FilterSearchControl>
      {loading ? <CircularProgress size={16} /> : <Icon>search</Icon>}
      <FilterSearchInput
        inputRef={inputRef}
        placeholder={placeholder}
        value={searchString}
        onChange={(e) => onChange(e.target.value)}
        onBlur={() => {
          if (isMobileXS && !searchString) {
            setExpanded(false);
          }
        }}
        inputProps={{ 'aria-label': 'Search' }}
      />
      {!!searchString && (
        <IconButton onClick={clearSearch} size="small" aria-label="Clear search">
          <Icon>clear</Icon>
        </IconButton>
      )}
    </FilterSearchControl>
  );
}

const COMPACT_RATINGS = [
  { value: 'all', label: 'All' },
  { value: 'good', label: 'Best Practices', icon: 'up' },
  { value: 'bad', label: 'Quality Opportunities', icon: 'down' },
];

const EXPANDED_RATINGS = [
  ...COMPACT_RATINGS,
  { value: 'none', label: 'Untagged', icon: 'none' },
];

const ratingIcon = (kind) => {
  if (kind === 'up') {
    return (
      <RatingIconWrap>
        <Icon>thumb_up</Icon>
      </RatingIconWrap>
    );
  }
  if (kind === 'down') {
    return (
      <RatingIconWrap className="neg">
        <Icon>thumb_down</Icon>
      </RatingIconWrap>
    );
  }
  if (kind === 'none') {
    return <Icon>cancel_presentation</Icon>;
  }
  return null;
};

export function FilterRating({ value = 'all', onChange, expanded = false }) {
  const options = expanded ? EXPANDED_RATINGS : COMPACT_RATINGS;
  return (
    <FilterRatingGroup
      exclusive
      size="small"
      color="primary"
      value={value}
      onChange={(e, next) => {
        if (next == null) return;
        onChange(next);
      }}
      aria-label="Rating filter"
    >
      {options.map((opt) => (
        <ToggleButton key={opt.value} value={opt.value} aria-label={opt.label}>
          {opt.icon ? ratingIcon(opt.icon) : opt.label}
        </ToggleButton>
      ))}
    </FilterRatingGroup>
  );
}

export const photoTagToRating = (tag) => {
  if (tag === undefined) return 'all';
  if (tag === 1) return 'good';
  if (tag === 0) return 'bad';
  if (tag === 'both') return 'both';
  if (tag === null) return 'none';
  return 'all';
};

export const ratingToPhotoTag = (rating) => {
  if (rating === 'all') return undefined;
  if (rating === 'good') return 1;
  if (rating === 'bad') return 0;
  if (rating === 'both') return 'both';
  if (rating === 'none') return null;
  return undefined;
};
